import React, { useRef, useEffect, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import MapTo from "../utils/mapto";
import iconArrowDown from "@images/icons/dropwdon-accordion.svg";
import GTM from '../utils/GTM';

/**
 * Accordion Component
 * 
 * This component can be used in two ways:
 * 
 * 1. With 'items' prop: For multiple accordion sections
 *    Example: <Accordion items={[{id: '1', label: 'Section 1', items: [...]}, ...]} />
 * 
 * 2. With 'title' and 'content' props: For a single accordion section
 *    Example: <Accordion title="Section Title" content={<div>Content here</div>} />
 * 
 * @param {Object} props
 * @param {Array} [props.items] - Array of accordion items (for multiple sections)
 * @param {string} [props.title] - Title for single accordion section
 * @param {ReactNode} [props.content] - Content for single accordion section
 * @param {boolean} [props.defaultOpen=false] - Whether the accordion is open by default
 * @param {Function} [props.onToggle] - Callback when accordion is toggled
 * @param {string} [props.className] - Additional CSS classes
 */
const Accordion = ({ 
  items, 
  className, 
  title, 
  content, 
  defaultOpen = false, 
  onToggle, 
  ...rest 
}) => {
  const accordionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInsideTabsContainer, setIsInsideTabsContainer] = useState(false);

  // Check if inside tabs-container
  useEffect(() => {
    const checkParentForTabsContainer = (element) => {
      if (!element || element === document.body) return false;
      if (element.classList.contains('tabs-container') || element.classList.contains('accordion-light')) return true;
      return checkParentForTabsContainer(element.parentElement);
    };

    if (accordionRef.current) {
      setIsInsideTabsContainer(checkParentForTabsContainer(accordionRef.current));
    }
  }, []);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Datalayer
  useEffect(() => {
    GTM.trackAccordion();
  }, []);

  // Render content for items-based accordion
  const renderItemsContent = (items) => {
    if (!items?.length) return null;
    
    return items.map((item) => (
      <MapTo 
        items={item} 
        key={item.id} 
        className={`accordion-content-item ${item.class === "text-p" ? "font-normal" : ""}`} 
      />
    ));
  };

  // Render a single accordion section
  const renderSingleAccordion = () => {
    return (
      <Disclosure defaultOpen={defaultOpen}>
        {({ open }) => {
          if (onToggle) {
            onToggle(open);
          }

          return (
            <div className="accordion-item">
              <DisclosureButton 
                className={`accordion-button flex gap-2.5 justify-start items-center w-full px-0 ${open ? "pb-6" : ""}`}
              >
                {title}
              </DisclosureButton>
              <div className="accordion-content overflow-hidden">
                <AnimatePresence>
                  {open && (
                    <DisclosurePanel
                      static
                      as={motion.div}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="origin-top"
                    >
                      {content}
                    </DisclosurePanel>
                  )}
                </AnimatePresence>
              </div>
            </div>
          );
        }}
      </Disclosure>
    );
  };

  // Render multiple accordion sections
  const renderMultipleAccordions = () => {
    return items?.map((panel) => (
      <Disclosure key={panel.id}>
        {({ open }) => (
          <div className="accordion-item">
            <DisclosureButton 
              className={`accordion-button flex gap-2.5 justify-start items-center w-full px-0`}
            >
              <span className={`${isInsideTabsContainer ? 'text-light_green' : 'text-dark_green'} text-p font-bold`}>
                {panel.label}
              </span>
              <span className="accordion-arrow flex items-center justify-center w-[32px] h-[32px] rounded-full border-gradient-light">
                <img 
                  src={iconArrowDown} 
                  alt="arrow-down" 
                  className={`${!open ? "rotate-180" : ""}`} 
                />
              </span>
            </DisclosureButton>
            <div className="accordion-content overflow-hidden">
              <AnimatePresence>
                {open && (
                  <DisclosurePanel
                    static
                    as={motion.div}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="origin-top pt-6"
                  >
                    {renderItemsContent(panel.items)}
                  </DisclosurePanel>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </Disclosure>
    ));
  };

  return (
    <div ref={accordionRef} className={`accordion-container ${className || ""}`} {...rest}>
      {items ? renderMultipleAccordions() : renderSingleAccordion()}
    </div>
  );
};

export default Accordion;
